import { clone, errors, runtime } from './runtime';
import { SCHEMA_VERSION } from './storage';

/**
 * Collections.
 *
 * The web app stores each collection under its own IndexedDB key rather than
 * one giant document. Writing a single 8MB blob every time an HR officer ticks
 * a checkbox is what makes prototypes feel slow, and it turns two concurrent
 * mutations into a lost update.
 *
 * Every read returns a deep copy. Nothing outside this module holds a reference
 * to stored data.
 */

export const COLLECTIONS = [
  'employees',
  'departments',
  'teams',
  'positions',
  'jobLevels',
  'workLocations',
  'costCenters',
  'shifts',
  'scheduleAssignments',
  'scheduleChangeRequests',
  'attendanceRecords',
  'attendanceCorrections',
  'securityReviews',
  'timesheets',
  'leaveTypes',
  'leaveRequests',
  'leaveBalances',
  'overtimeRequests',
  'payrollRuns',
  'payrollLines',
  'payslips',
  'payrollConfig',
  'compensation',
  'benefitPlans',
  'benefitEnrollments',
  'loans',
  'requisitions',
  'candidates',
  'onboardingTemplates',
  'onboardingCases',
  'probationReviews',
  'reviewCycles',
  'performanceReviews',
  'courses',
  'trainingSessions',
  'trainingRecords',
  'documents',
  'documentTypes',
  'policies',
  'acknowledgements',
  'announcements',
  'assets',
  'relationsCases',
  'offboardingCases',
  'systemUsers',
  'roles',
  'notifications',
  'auditEntries',
  'settings',
  'holidays',
] as const;

export type CollectionName = (typeof COLLECTIONS)[number];

interface Envelope<T> {
  version: number;
  rows: T[];
}

const memory = new Map<CollectionName, unknown[]>();
let loaded = false;
let writeQueue: Promise<void> = Promise.resolve();

function key(name: CollectionName): string {
  return `collection:${name}`;
}

/**
 * Load every collection into memory.
 *
 * Called once at bootstrap. Services read synchronously afterwards, which is
 * what lets a filter over 2,000 attendance records feel instant while still
 * persisting every write.
 */
export async function loadCollections(
  seed: () => Record<CollectionName, unknown[]>,
): Promise<{ seeded: boolean }> {
  if (loaded) return { seeded: false };

  const storedVersion = await runtime.storage.get<number>('schema-version');
  const needsSeed = storedVersion !== SCHEMA_VERSION;

  if (needsSeed) {
    const data = seed();
    for (const name of COLLECTIONS) {
      memory.set(name, data[name] ?? []);
    }
    await persistAll();
    await runtime.storage.set('schema-version', SCHEMA_VERSION);
    loaded = true;
    return { seeded: true };
  }

  for (const name of COLLECTIONS) {
    const envelope = await runtime.storage.get<Envelope<unknown>>(key(name));
    // A collection missing from storage is not corruption — it is a collection
    // added since the data was written. Treat it as empty rather than failing.
    memory.set(name, envelope?.rows ?? []);
  }
  loaded = true;
  return { seeded: false };
}

export function isLoaded(): boolean {
  return loaded;
}

function assertLoaded(): void {
  if (!loaded) {
    throw errors.precondition(
      'The MicaraHR data store has not been loaded. Call loadCollections() during bootstrap.',
    );
  }
}

/** All rows in a collection, as copies. */
export function readAll<T>(name: CollectionName): T[] {
  assertLoaded();
  return clone((memory.get(name) ?? []) as T[]);
}

/** A single row by id, or null. */
export function readOne<T extends { id: string }>(name: CollectionName, id: string): T | null {
  assertLoaded();
  const row = ((memory.get(name) ?? []) as T[]).find((entry) => entry.id === id);
  return row ? clone(row) : null;
}

/**
 * Replace a collection wholesale and persist it.
 *
 * Writes are serialised so two rapid mutations cannot interleave and lose one
 * another — the failure mode that makes a prototype's data mysteriously revert.
 */
export async function writeAll<T>(name: CollectionName, rows: T[]): Promise<void> {
  assertLoaded();
  memory.set(name, clone(rows) as unknown[]);
  await enqueue(name);
}

/** Insert a row at the front, so the newest record is the first one seen. */
export async function insert<T extends { id: string }>(name: CollectionName, row: T): Promise<T> {
  const rows = (memory.get(name) ?? []) as T[];
  memory.set(name, [clone(row), ...rows]);
  await enqueue(name);
  return clone(row);
}

/** Apply a change to one row. Throws `not_found` rather than silently doing nothing. */
export async function update<T extends { id: string }>(
  name: CollectionName,
  id: string,
  change: (row: T) => T,
  noun = 'record',
): Promise<T> {
  const rows = (memory.get(name) ?? []) as T[];
  const index = rows.findIndex((row) => row.id === id);
  if (index === -1) throw errors.notFound(noun);

  const next = change(clone(rows[index]!));
  const copy = [...rows];
  copy[index] = next;
  memory.set(name, copy);
  await enqueue(name);
  return clone(next);
}

export async function remove(name: CollectionName, id: string): Promise<void> {
  const rows = (memory.get(name) ?? []) as { id: string }[];
  memory.set(
    name,
    rows.filter((row) => row.id !== id),
  );
  await enqueue(name);
}

function enqueue(name: CollectionName): Promise<void> {
  writeQueue = writeQueue.then(async () => {
    try {
      await runtime.storage.set<Envelope<unknown>>(key(name), {
        version: SCHEMA_VERSION,
        rows: (memory.get(name) ?? []) as unknown[],
      });
    } catch {
      // A storage failure must not lose the session. The in-memory copy stays
      // authoritative and the interface keeps working; the Simulation Centre
      // exposes this path deliberately.
    }
  });
  return writeQueue;
}

async function persistAll(): Promise<void> {
  for (const name of COLLECTIONS) await enqueue(name);
  await writeQueue;
}

/** Re-seed everything. Used by the Simulation Centre's "Reset demo data". */
export async function resetCollections(
  seed: () => Record<CollectionName, unknown[]>,
): Promise<void> {
  const data = seed();
  for (const name of COLLECTIONS) memory.set(name, data[name] ?? []);
  loaded = true;
  await persistAll();
  await runtime.storage.set('schema-version', SCHEMA_VERSION);
}

/** Drop the in-memory cache without touching storage. Used by tests. */
export function clearCollectionCache(): void {
  memory.clear();
  loaded = false;
}

/** Row counts, shown in the Simulation Centre. */
export function collectionSizes(): Record<string, number> {
  const sizes: Record<string, number> = {};
  for (const name of COLLECTIONS) sizes[name] = (memory.get(name) ?? []).length;
  return sizes;
}

/* -------------------------------------------------------------------------- */
/* Query helpers                                                              */
/* -------------------------------------------------------------------------- */

export interface PageRequest {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
}

export interface Page<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Sort, filter and paginate in one place.
 *
 * Every list service uses this, so "search then sort then page" means the same
 * thing in every module — and when these become real API calls, the request
 * shape is already the one the backend will implement.
 */
export function paginate<T extends Record<string, unknown>>(
  rows: T[],
  request: PageRequest,
  searchable: (row: T) => string,
): Page<T> {
  let working = rows;

  const needle = request.search?.trim().toLowerCase();
  if (needle) {
    working = working.filter((row) => searchable(row).toLowerCase().includes(needle));
  }

  if (request.sortBy) {
    const direction = request.sortDir === 'desc' ? -1 : 1;
    const field = request.sortBy;
    working = [...working].sort((a, b) => {
      const left = a[field];
      const right = b[field];
      if (left === right) return 0;
      if (left === null || left === undefined) return 1;
      if (right === null || right === undefined) return -1;
      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * direction;
      }
      return String(left).localeCompare(String(right)) * direction;
    });
  }

  const page = Math.max(1, request.page ?? 1);
  const pageSize = Math.max(1, request.pageSize ?? 25);
  const start = (page - 1) * pageSize;

  return {
    rows: working.slice(start, start + pageSize),
    total: working.length,
    page,
    pageSize,
  };
}
